import { defineComponent as _defineComponent } from 'vue'
import { unref as _unref, createVNode as _createVNode, createElementVNode as _createElementVNode, renderList as _renderList, Fragment as _Fragment, openBlock as _openBlock, createElementBlock as _createElementBlock, toDisplayString as _toDisplayString, createTextVNode as _createTextVNode, withCtx as _withCtx, withModifiers as _withModifiers, createBlock as _createBlock, createCommentVNode as _createCommentVNode, pushScopeId as _pushScopeId, popScopeId as _popScopeId } from "vue"

const _withScopeId = n => (_pushScopeId("data-v-7e0ce4d4"),n=n(),_popScopeId(),n)
const _hoisted_1 = { class: "project-settings-admin-page" }
const _hoisted_2 = /*#__PURE__*/ _withScopeId(() => /*#__PURE__*/_createElementVNode("h2", { class: "project-settings-admin-page__title" }, "Администраторы", -1))
const _hoisted_3 = { class: "project-settings-admin-page__content" }
const _hoisted_4 = { class: "project-settings-admin-page__users-list" }
const _hoisted_5 = { class: "project-settings-admin-page__user-item-left" }
const _hoisted_6 = { class: "project-settings-admin-page__sign-name" }
const _hoisted_7 = { class: "project-settings-admin-page__user-item-right" }
const _hoisted_8 = /*#__PURE__*/ _withScopeId(() => /*#__PURE__*/_createElementVNode("div", { class: "project-settings-admin-page__checkbox-tooltip-content" }, " Может формировать очередность задач ", -1))

import { computed, inject, onBeforeMount, onMounted, Ref, ref } from 'vue';
import { useRouter } from 'vue-router';
import { CurrentUser, UserRoleEnum } from 'tir-pm-user-core';
import { TirPmSearchInput, TirPmSearchInputContentLine } from 'tir-pm-search-input';
import {
  TirPmIconView,
  TirPmButton,
  TirPmButtonSizeEnum,
  TirPmButtonVariantEnum,
  TirCheckbox,
  TirPmNotificationEventBus,
  TirPmNotificationModel,
  TirPmPopover,
  PopoverPositionEnum,
  PopoverColorEnum,
  TirPmTooltipElement,
} from 'tir-components';
import { TrashIcon } from 'tir-style-system/icons/outline';

import { AdminModel, SaveAdminsModel, UserModel } from './models';
import { ProjectModel } from '@/models';
import { AdapterService } from './adapter';
import { DeleteProjectAdminModal } from './components';

/**Адаптер */

export default /*#__PURE__*/_defineComponent({
  __name: 'project-settings-admin-page',
  setup(__props) {

const adapter = new AdapterService();
/**Маршруты */
const router = useRouter();

// #region refs
/**Ссылка на модальное окно удаления администратора из списка */
const $deleteAdminModal = ref<InstanceType<typeof DeleteProjectAdminModal>>();
/**Информация о проекте */
const project = inject('project') as Ref<ProjectModel>;
/**Загрузка при инициализации компонента */
const isInitLoading = ref(false);
/**Строка поиска */
const searchStr = ref<string>();
/**Список администраторов */
const admins = ref(new Array<AdminModel>());
/**Список пользователей */
const users = ref(new Array<UserModel>());
// #endregion refs

// #region computed
/**Владелец */
const isOwner = computed(() => CurrentUser.Roles.includes(UserRoleEnum.Owner));
/**Админ проекта */
const isProjectAdmin = computed(() => project.value.Admins.some((a) => a.Id == CurrentUser.Id));
/**Отсортированный список администраторов */
const sortAdmins = computed(() =>
  admins.value.sort((a, b) => a.FirstName.localeCompare(b.FirstName)),
);
/**Видимые пользователи в списке на добавление админов проекта */
const visibleUsers = computed(() => {
  let _users = users.value.filter((user) => !admins.value.some((admin) => user.Id === admin.Id));

  if (searchStr.value?.trim()) {
    _users = _users.filter((user) =>
      user.Initials.toLocaleLowerCase().includes(searchStr.value.trim().toLocaleLowerCase()),
    );
  }

  return _users.sort((a, b) => a.Initials.localeCompare(b.Initials));
});
// #endregion computed

// #region Хуки жизненного цикла
onMounted(() => {
  getData();
});
/**Перед рендером */
onBeforeMount(() => {
  if (!isOwner.value && !isProjectAdmin.value) {
    router.push('/');
  }
});
// #endregion Хуки жизненного цикла

// #region Функции
/**
 * Добавить уведомление
 * @param message Сообщение
 * @param isError Успех/ошибка
 */
const addNotification = (message: string, isError: boolean = false) => {
  TirPmNotificationEventBus.emit(
    'add',
    new TirPmNotificationModel({
      title: message,
      type: isError ? 'error' : 'success',
      group: 'general',
      life: isError ? 5000 : 1000,
      closable: true,
    }),
  );
};
/**
 * * Добавить администратора проекта
 * @param user Пользователь
 */
const addAdmin = async (user: UserModel) => {
  const admin = new AdminModel(user);
  const request = new SaveAdminsModel({
    ProjectId: project.value.Id,
    Admins: [...admins.value, admin].map((admin) => ({
      Id: admin.Id,
      CanCustomizeOrder: admin.CanCustomizeOrder,
    })),
    RemoveQueueTasksAccess: false,
    RemoveTaskObserver: false,
    RemoveTaskPerformer: false,
  });
  const resp = await adapter.saveProjectAdmins(request);
  if (resp.IsSuccess) {
    admins.value.push(admin);
    addNotification('Список успешно обновлен', false);
  } else {
    addNotification(resp.ErrorMessage ?? 'Произошла ошибка');
    setTimeout(() => getAdmins(), 1000); // Актуализация списка через 500 мс после ошибки
  }
};

/**
 * Обновить флаг возможности формирования очередности
 * @param admin Админ
 * @param canCustomizeOrder Значение
 */
const updateCanCustomizeOrder = async (admin: AdminModel, canCustomizeOrder: boolean) => {
  const index = admins.value.findIndex((_admin) => admin.Id === _admin.Id);
  if (index === -1) return;

  admins.value[index].CanCustomizeOrder = canCustomizeOrder;
  const request = new SaveAdminsModel({
    ProjectId: project.value.Id,
    Admins: admins.value.map((admin) => ({
      Id: admin.Id,
      CanCustomizeOrder: admin.CanCustomizeOrder,
    })),
    RemoveQueueTasksAccess: false,
    RemoveTaskObserver: false,
    RemoveTaskPerformer: false,
  });
  const resp = await adapter.saveProjectAdmins(request);
  if (resp.IsSuccess) {
    addNotification('Список успешно обновлен', false);
  } else {
    addNotification(resp.ErrorMessage ?? 'Произошла ошибка');
    setTimeout(() => getAdmins(), 1000); // Актуализация списка через 1 секунду после ошибки
  }
};
/**
 * Удалить администратора из локального списка
 * @param adminId ID администратора
 */
const deleteAdminLocal = (adminId: number) => {
  admins.value = admins.value.filter((admin) => admin.Id !== adminId);
};
/**
 * При успешном удалении администратора из списка
 * @param adminId ID администратора
 */
const onSuccessDeleteAdmin = (adminId: number) => {
  deleteAdminLocal(adminId);
};

/**Получить список администраторов */
const getAdmins = async () => {
  const resp = await adapter.getProjectAdmins(project.value.Id);

  if (resp.IsSuccess) {
    admins.value = resp.Value;
  }
};
/**Получить список пользователей для назначение админов проекта */
const getUsers = async () => {
  const resp = await adapter.getUsersAssignProjectAdmins();

  if (resp.IsSuccess) {
    users.value = resp.Value;
  }
};
/**Получить данные */
const getData = async () => {
  isInitLoading.value = true;
  await Promise.all([getAdmins(), getUsers()]);
  isInitLoading.value = false;
};
// #endregion Функции

return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createElementBlock("div", _hoisted_1, [
    _createVNode(_unref(DeleteProjectAdminModal), {
      ref_key: "$deleteAdminModal",
      ref: $deleteAdminModal,
      admins: admins.value,
      "project-id": _unref(project).Id,
      onOnSuccessDelete: onSuccessDeleteAdmin
    }, null, 8, ["admins", "project-id"]),
    _hoisted_2,
    _createElementVNode("div", _hoisted_3, [
      _createVNode(_unref(TirPmSearchInput), {
        style: {"width":"100%"},
        label: "Администраторы",
        placeholder: "",
        isWithPopup: true,
        modelValue: searchStr.value,
        "onUpdate:modelValue": _cache[0] || (_cache[0] = ($event: any) => ((searchStr).value = $event)),
        isDisabled: !isOwner.value
      }, {
        content: _withCtx(() => [
          (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(visibleUsers.value, (user) => {
            return (_openBlock(), _createBlock(_unref(TirPmSearchInputContentLine), {
              key: user.Id,
              onClick: _withModifiers(($event: any) => (addAdmin(user)), ["stop"])
            }, {
              leftSlot: _withCtx(() => [
                _createVNode(_unref(TirPmIconView), {
                  size: 24,
                  isRounded: "",
                  isBordered: false,
                  colorCode: user.AvatarColor,
                  imageLoadUrl: user.Photo
                }, {
                  mainSlot: _withCtx(() => [
                    _createTextVNode(_toDisplayString(user.AvatarInitials), 1)
                  ]),
                  _: 2
                }, 1032, ["colorCode", "imageLoadUrl"])
              ]),
              default: _withCtx(() => [
                _createTextVNode(" " + _toDisplayString(user.FirstName) + " " + _toDisplayString(user.MiddleName) + " " + _toDisplayString(user.LastName), 1)
              ]),
              _: 2
            }, 1032, ["onClick"]))
          }), 128))
        ]),
        _: 1
      }, 8, ["modelValue", "isDisabled"])
    ]),
    _createElementVNode("div", _hoisted_4, [
      (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(sortAdmins.value, (adminUser) => {
        return (_openBlock(), _createElementBlock("div", {
          class: "project-settings-admin-page__user-item",
          key: adminUser.Id
        }, [
          _createElementVNode("div", _hoisted_5, [
            _createVNode(_unref(TirPmIconView), {
              size: 48,
              isRounded: "",
              isBordered: false,
              colorCode: adminUser.AvatarColor,
              imageLoadUrl: adminUser.Photo
            }, {
              mainSlot: _withCtx(() => [
                _createTextVNode(_toDisplayString(adminUser.AvatarInitials), 1)
              ]),
              _: 2
            }, 1032, ["colorCode", "imageLoadUrl"]),
            _createElementVNode("div", _hoisted_6, _toDisplayString(adminUser.Initials), 1)
          ]),
          _createElementVNode("div", _hoisted_7, [
            (isOwner.value)
              ? (_openBlock(), _createBlock(_unref(TirPmButton), {
                  key: 0,
                  class: "project-settings-admin-page__delete-button",
                  size: _unref(TirPmButtonSizeEnum).Small,
                  variant: _unref(TirPmButtonVariantEnum).LinkState,
                  onClick: ($event: any) => ($deleteAdminModal.value?.open(adminUser))
                }, {
                  leftSlot: _withCtx(() => [
                    _createVNode(_unref(TrashIcon))
                  ]),
                  _: 2
                }, 1032, ["size", "variant", "onClick"]))
              : _createCommentVNode("", true),
            _createVNode(_unref(TirPmTooltipElement), null, {
              content: _withCtx(() => [
                _hoisted_8
              ]),
              default: _withCtx(() => [
                _createVNode(_unref(TirCheckbox), {
                  isDisabled: !isOwner.value,
                  "model-value": adminUser.CanCustomizeOrder,
                  "onUpdate:modelValue": ($event: any) => (updateCanCustomizeOrder(adminUser, $event))
                }, null, 8, ["isDisabled", "model-value", "onUpdate:modelValue"])
              ]),
              _: 2
            }, 1024)
          ])
        ]))
      }), 128))
    ])
  ]))
}
}

})